import { useEffect, useMemo, useState } from 'react';
import {
  fetchInstagramKeywordRules,
  saveInstagramKeywordRule,
  setInstagramKeywordRuleEnabled
} from '../../../services/instagram-keywords-api.js';

const EMPTY_FORM = {
  id: '',
  keyword: '',
  matchMode: 'exact',
  mediaId: '',
  publicReply: '¡Te lo mandé por privado! 😊',
  privateReply: '',
  enabled: true
};

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function matches(rule, text, mediaId) {
  if (!rule || !rule.enabled) return false;
  if (rule.mediaId && String(rule.mediaId) !== String(mediaId || '')) return false;
  const source = normalizeText(text);
  const keyword = normalizeText(rule.keyword);
  return rule.matchMode === 'contains' ? source.includes(keyword) : source === keyword;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('es-CL');
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontWeight: 700 }}>{label}</span>
      {children}
      {hint ? <small style={{ opacity: 0.72 }}>{hint}</small> : null}
    </label>
  );
}

export default function InstagramKeywordsPage() {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [previewText, setPreviewText] = useState('podcast');
  const [previewMediaId, setPreviewMediaId] = useState('');

  async function reload() {
    setLoading(true);
    setError('');
    try {
      setRules(await fetchInstagramKeywordRules());
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudieron cargar las palabras clave.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const previewRule = useMemo(
    () => rules.find((rule) => matches(rule, previewText, previewMediaId)),
    [rules, previewText, previewMediaId]
  );

  function edit(rule) {
    setForm({
      id: rule.id || '',
      keyword: rule.keyword || '',
      matchMode: rule.matchMode || 'exact',
      mediaId: rule.mediaId || '',
      publicReply: rule.publicReply || '',
      privateReply: rule.privateReply || '',
      enabled: rule.enabled !== false
    });
    setNotice('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!form.keyword.trim()) {
      setError('Ingresá una palabra clave.');
      return;
    }
    if (!form.publicReply.trim() && !form.privateReply.trim()) {
      setError('Definí una respuesta pública, un mensaje privado o ambos.');
      return;
    }
    setSaving(true);
    try {
      await saveInstagramKeywordRule({
        ...(form.id ? { id: form.id } : {}),
        keyword: form.keyword.trim(),
        matchMode: form.matchMode,
        ...(form.mediaId.trim() ? { mediaId: form.mediaId.trim() } : {}),
        ...(form.publicReply.trim() ? { publicReply: form.publicReply.trim() } : {}),
        ...(form.privateReply.trim() ? { privateReply: form.privateReply.trim() } : {}),
        enabled: form.enabled
      });
      setForm(EMPTY_FORM);
      setNotice('Regla guardada. Los comentarios nuevos usarán esta configuración.');
      await reload();
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudo guardar la regla.');
    } finally {
      setSaving(false);
    }
  }

  async function toggle(rule) {
    setError('');
    setNotice('');
    try {
      await setInstagramKeywordRuleEnabled(rule.id, !rule.enabled);
      setNotice(rule.enabled ? 'Regla pausada.' : 'Regla activada.');
      await reload();
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudo cambiar el estado.');
    }
  }

  return (
    <main style={{ display: 'grid', gap: 24, paddingBottom: 40 }}>
      <header>
        <p className="analytics-eyebrow">Instagram</p>
        <h1 style={{ marginBottom: 8 }}>Palabras clave y respuestas automáticas</h1>
        <p style={{ margin: 0, maxWidth: 820, opacity: 0.76 }}>
          Configurá llamados a la acción del tipo “comentá PODCAST y te mando la información por privado”.
          El comentario dispara la respuesta definida y, si la persona contesta el DM, Ana continúa la conversación normal.
        </p>
      </header>

      {error ? <div role="alert" style={{ padding: 14, border: '1px solid currentColor', borderRadius: 12 }}>{error}</div> : null}
      {notice ? <div role="status" style={{ padding: 14, border: '1px solid currentColor', borderRadius: 12 }}>{notice}</div> : null}

      <section style={{ padding: 20, border: '1px solid rgba(127,127,127,.3)', borderRadius: 16 }}>
        <h2 style={{ marginTop: 0 }}>{form.id ? 'Editar regla' : 'Nueva regla'}</h2>
        <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
            <Field label="Palabra clave" hint="Ej.: podcast, iva, inversion">
              <input value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} placeholder="podcast" maxLength={120} />
            </Field>
            <Field label="Coincidencia" hint="Exacta: el comentario debe ser la palabra. Contiene: puede estar dentro de una frase.">
              <select value={form.matchMode} onChange={(e) => setForm({ ...form, matchMode: e.target.value })}>
                <option value="exact">Exacta</option>
                <option value="contains">Contiene</option>
              </select>
            </Field>
            <Field label="ID del reel/publicación" hint="Opcional. Vacío = funciona en cualquier publicación.">
              <input value={form.mediaId} onChange={(e) => setForm({ ...form, mediaId: e.target.value })} placeholder="Todos los reels" maxLength={120} />
            </Field>
          </div>

          <Field label="Respuesta pública" hint="Opcional. Se publica debajo del comentario.">
            <textarea rows={3} value={form.publicReply} onChange={(e) => setForm({ ...form, publicReply: e.target.value })} maxLength={1000} />
          </Field>

          <Field label="Mensaje privado" hint="Podés escribir texto y pegar uno o más links. Se envía por DM a quien comentó.">
            <textarea rows={6} value={form.privateReply} onChange={(e) => setForm({ ...form, privateReply: e.target.value })} placeholder="Gracias por comentar. Te dejo la información acá: https://..." maxLength={2000} />
          </Field>

          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            <span>Dejar activa al guardar</span>
          </label>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="submit" disabled={saving}>{saving ? 'Guardando…' : form.id ? 'Guardar cambios' : 'Crear regla'}</button>
            {form.id ? <button type="button" onClick={() => setForm(EMPTY_FORM)}>Cancelar edición</button> : null}
          </div>
        </form>
      </section>

      <section style={{ padding: 20, border: '1px solid rgba(127,127,127,.3)', borderRadius: 16 }}>
        <h2 style={{ marginTop: 0 }}>Probar sin enviar</h2>
        <p style={{ opacity: 0.75 }}>Esta prueba solo simula qué regla coincidiría. No publica ni manda mensajes en Instagram.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <input value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="Comentario de prueba" />
          <input value={previewMediaId} onChange={(e) => setPreviewMediaId(e.target.value)} placeholder="Media ID opcional" />
        </div>
        <div style={{ marginTop: 14, padding: 14, borderRadius: 12, background: 'rgba(127,127,127,.08)' }}>
          {previewRule ? (
            <>
              <strong>Coincide con “{previewRule.keyword}”</strong>
              <p><b>Público:</b> {previewRule.publicReply || 'No responde públicamente'}</p>
              <p style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}><b>Privado:</b> {previewRule.privateReply || 'No envía DM'}</p>
            </>
          ) : <span>No hay una regla activa que coincida con esta prueba.</span>}
        </div>
      </section>

      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <h2>Reglas configuradas</h2>
          <button type="button" onClick={() => void reload()} disabled={loading}>{loading ? 'Cargando…' : 'Actualizar'}</button>
        </div>
        {!loading && rules.length === 0 ? (
          <div style={{ padding: 20, border: '1px dashed rgba(127,127,127,.4)', borderRadius: 16 }}>
            Todavía no hay palabras clave configuradas.
          </div>
        ) : null}
        <div style={{ display: 'grid', gap: 12 }}>
          {rules.map((rule) => (
            <article key={rule.id} style={{ padding: 16, border: '1px solid rgba(127,127,127,.3)', borderRadius: 14, opacity: rule.enabled ? 1 : 0.62 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: '0 0 6px' }}>{rule.keyword}</h3>
                  <div style={{ fontSize: 13, opacity: 0.72 }}>
                    {rule.matchMode === 'contains' ? 'Contiene' : 'Exacta'} · {rule.mediaId ? 'Reel ' + rule.mediaId : 'Todos los reels'} · {rule.enabled ? 'Activa' : 'Pausada'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => edit(rule)}>Editar</button>
                  <button type="button" onClick={() => void toggle(rule)}>{rule.enabled ? 'Pausar' : 'Activar'}</button>
                </div>
              </div>
              {rule.publicReply ? <p><b>Público:</b> {rule.publicReply}</p> : null}
              {rule.privateReply ? <p style={{ whiteSpace: 'pre-wrap' }}><b>DM:</b> {rule.privateReply}</p> : null}
              <small style={{ opacity: 0.65 }}>Actualizada: {formatDate(rule.updatedAt)}{rule.updatedBy ? ' · ' + rule.updatedBy : ''}</small>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
